import React, { useEffect, useState } from 'react';
import { useAppState } from '../context/AppStateContext';
import { SupportDataService, exitSupportSession, formatSupportExpiry } from '../services/supportDataService';
import { getSelectedOrgId } from '../services/selectedOrg';
import { setSupportDiagnosticsAllowed } from '../services/supportDiagnosticsService';

/** How often to confirm the grant is still live, so a revoke ends the session promptly. */
const CHECK_MS = 60 * 1000;

/**
 * Header marker for a Rustle It support session: which organisation, until
 * when, and the way out. Also ends the session when the grant does — expiry
 * or an Admin's revoke — rather than leaving support on a page whose every
 * request now fails.
 */
const SupportSessionBanner: React.FC = () => {
  const { activeUser, selectedOrganisation } = useAppState();
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const isSupport = activeUser?.isSupport === true;

  useEffect(() => {
    if (!isSupport) return;
    let cancelled = false;
    const check = async () => {
      try {
        const orgId = Number(getSelectedOrgId());
        const { organisations } = await SupportDataService.fetchMyOrganisations();
        const live = organisations.find(o => o.id === orgId);
        if (cancelled) return;
        if (!live) {
          sessionStorage.setItem('supportSessionEnded', 'Support access to this organisation has ended.');
          exitSupportSession();
          return;
        }
        setExpiresAt(live.expiresAt);
        setSupportDiagnosticsAllowed(live.allowDiagnostics === true);
      } catch {
        // A failed check is not proof the grant ended; the API still refuses
        // every request once it has.
      }
    };
    void check();
    const timer = window.setInterval(check, CHECK_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [isSupport]);

  if (!isSupport) return null;

  return (
    <div className="support-session-banner" role="status">
      <i className="fas fa-life-ring" aria-hidden="true" />
      <span>
        Support session{selectedOrganisation ? ` · ${selectedOrganisation.name}` : ''}
        {expiresAt ? ` · until ${formatSupportExpiry(expiresAt)}` : ''}
      </span>
      <button type="button" className="support-session-banner__exit" onClick={exitSupportSession}>
        Exit
      </button>
    </div>
  );
};

export default SupportSessionBanner;
